import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useParams } from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";
import GameProposalTestPlanSaveAPI from "./Data/GameProposalTestPlanSaveAPI";
import GameProposalCreationAPI from "./Data/GameProposalCreationAPI";
import { GameProposalStepAvailabilityAPI } from "../AddTestApi";
import { SuccessModal } from '../components/SuccessModal';
import { useRecoilValue } from "recoil";
import { gameProposalIdState } from "src/recoil/atoms/gameProposalState";
import CustomNavLink from '../components/CustomNavLink';
import { GameProposalStepAvailabilityResponse } from "../../types/Response";
import { GameProposalDetailsAPI, LabsGameProposalTestPlanDetailsAPI } from "../../TestManagementApi";


const Main = styled.main`
    background-color: #f8f9fa;
  width: 100%;
  min-width: 1280px;
  max-width: 1920px;
  height: 100vh;
  padding-left: 195px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const TopWrapper = styled.div`
  padding-left: 80px;
  border-bottom: 1px solid #ededed;
  min-height: 157px;
  display: flex;
  flex-direction: column;
  gap: 34px;
  padding-top: 30px;
  background-color: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const TopContainer = styled.div`
  // width: 1280px;
  width: 95%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TopTitle = styled.span`
  color: #2282e9;
  font-size: 20px;
  font-weight: 700;
`;

const BtnContainer = styled.div`
  display: flex;
  gap: 20px;
`;

const TopBtn = styled.button`
  width: 160px;
  height: 55px;
  border-radius: 5px;
  background-color: white;
  border: 1px solid #ededed;
  color: #c0c0c0;
  font-weight: 600;
  cursor: pointer;
`;

const PreviewBtn = styled(TopBtn)`
  color: white;
  background-color: #2282e9;
`;

interface SaveBtnProps {
  isActive: boolean;
}

const SaveBtn = styled(TopBtn) <SaveBtnProps>`
  color: ${({ isActive }) => isActive ? "#2282e9" : "#c0c0c0"};
  border: 1px solid ${({ isActive }) => isActive ? "#2282e9" : "#c0c0c0"};
  border-radius: 5px;
  cursor: ${({ isActive }) => isActive ? "pointer" : "not-allowed"};
  opacity: ${({ isActive }) => isActive ? "1" : "0.6"};
`;

const NavCotainer = styled.div`
  color: #c0c0c0;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  width: 1280px;
  height: 37px;
`;

const Nav = styled.div`
  width: 165px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const AccentNav = styled(Nav)`
  border-bottom: 2px solid #2282e9;
  color: #2282e9;
`;

const ContentWrapper = styled.div`
  padding-top: 70px;
  padding-left: 125px;
  padding-bottom: 309px;
  overflow-y: auto;
  height: calc(100vh - 157px);
`;

const Title = styled.span`
  font-weight: 600;
  font-size: 18px;
`;

const Required = styled.span`
  color: #2282e9;
  font-weight: 600;
  font-size: 18px;
`;

const SubTitle = styled.span`
  display: block;
  color: #9f9f9f;
  margin-top: 8px;
  margin-bottom: 20px;
  line-height: 26px;
`;

const LabelContainer = styled.div`
  display: flex;
  gap: 14px;
  margin-bottom: 100px;
`;

const PlatformButton = styled.button<{ isSelected: boolean; disabled?: boolean }>`
  display: flex;
  height: 60px;
  width: 209px;
  font-weight: 500;
  justify-content: center;
  align-items: center;
  padding: 15px 20px;
  border: 1px solid ${({ disabled, isSelected }) => {
    if (disabled && isSelected) return '#CCCCCC';  // 선택된 상태에서 비활성화된 경우 더 진한 테두리 색상
    else if (disabled) return '#CCCCCC';
    else return '#2282E9';
  }};
  color: ${({ isSelected, disabled }) => {
    if (isSelected && disabled) return '#f0f8ff';  // 선택된 상태에서 비활성화된 경우 약간 흐린 흰색
    else if (isSelected) return 'white';
    else if (disabled) return '#CCCCCC';
    else return '#2282E9';
  }};
  border-radius: 5px;
  background-color: ${({ isSelected, disabled }) => {
    if (isSelected && disabled) return '#8E8E8E';  // 선택된 상태에서 비활성화된 경우 약간 흐린 파란색
    else if (isSelected) return '#2282E9';
    else if (disabled) return '#F5F5F5';
    else return 'white';
  }};
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  opacity: ${({ disabled, isSelected }) => disabled && !isSelected ? '0.7' : '1'};  // 선택된 상태면 불투명도 유지
`;

const NumInputContainer = styled.div<{ disabled?: boolean }>`
  border-radius: 5px;
  width: 458px;
  height: 62px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${({ disabled }) => disabled ? '#F5F5F5' : 'white'};
  padding: 6px;
  margin-bottom: 10px;
`;

const NumInput = styled.input<{ disabled?: boolean }>`
  font-weight: 600;
  text-align: center;
  outline: none;
  border: none;
  color: ${({ disabled }) => disabled ? '#CCCCCC' : 'inherit'};
  background-color: transparent;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'text'};
  
`;

const NumBtn = styled.div<{ disabled?: boolean }>`
  width: 50px;
  height: 50px;
  background-color: ${({ disabled }) => disabled ? '#EEEEEE' : '#f8f9fa'};
  border-radius: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  opacity: ${({ disabled }) => disabled ? '0.7' : '1'};
`;

const Minus = styled.div`
  background-color: #9f9f9f;
  border-radius: 3.2px;
  width: 26px;
  height: 3px;
`;
const Plus = styled.div`
  background-color: #9f9f9f;
  border-radius: 3.2px;
  height: 26px;
  width: 3px;
  position: absolute;
  top: 13px;
  left: 24px;
`;

const CheckBoxContainer = styled.div<{ disabled?: boolean }>`
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  border: 1px solid ${({ disabled }) => disabled ? '#ededed' : '#ededed'};
  background-color: ${({ disabled }) => disabled ? 'white' : 'white'};
  border-radius: 5px;
  margin-top: 10px;
  margin-bottom: 10px;
  width: 458px;
  height: 62px;
  display: flex;
  align-items: center;
  padding: 0px 12px;
  opacity: ${({ disabled }) => disabled ? '0.7' : '1'};
`;

const CheckBoxLabel = styled.label`
  width: 40px;
  height: 40px;
  border-radius: 5px;
  background-color: #f8f9fa;
  margin-right: 36px;
  position: relative;

  input[type="checkbox"]:checked + & {
    background-color: #2282E9;
  }
`;

const CheckBoxCheck = styled.div`
  width: 12px;
  height: 20px;
  rotate: 45deg;

  border-bottom: 3px solid #f8f9fa;
  border-right: 3px solid #f8f9fa;
  position: absolute;
  top: 7px;
  left: 15px;

  input[type="checkbox"]:checked > & {
    border-bottom: 3px solid white;
    border-right: 3px solid white;
  }
`;

const CheckBoxSpan = styled.span`
  font-weight: 500;
  color: #797979;
`;

const CheckInput = styled.input`
  display: none;
`;

const TooltipContainer = styled.div`
  position: relative;
  display: inline-flex;  // flex로 변경
  align-items: center;   // 중앙 정렬 추가
  width: 165px;
  height: 100%;         // 100%로 변경하여 부모 높이에 맞춤
`;

const Tooltip = styled.div<{ disabled?: boolean }>`
  visibility: hidden;
  position: absolute;
  bottom: calc(100% + 8px);  // 위치 조정
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 12px;
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  font-size: 12px;
  border-radius: 4px;
  white-space: nowrap;
  z-index: 1000;
  
  ${TooltipContainer}:hover & {
    visibility: ${props => !props.disabled ? 'hidden' : 'visible'};
  }

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-width: 5px;
    border-style: solid;
    border-color: rgba(0, 0, 0, 0.8) transparent transparent transparent;
  }
`;


// 플랫폼 타입 정의
type Platform = 'CBT' | 'NONE' | null;

type FormData = {
  gameTestType: string;
  desiredTesterCount: number;
  adultCheckRequired: boolean;
  recruitDays: number;
  testDays: number;
}

interface GameProposalTestPlanDetailsResponse {
  gameProposalId: number;
  gameTestType: string;
  desiredTesterCount: number;
  adultCheckRequired: boolean;
  recruitDays: number;
  testDays: number;
  rejections: Rejection;
}
interface Rejection {
  count: number;
  reasons: RejectionDetail;
}
interface RejectionDetail {
  recruitDays: string;
  adultCheckRequired: string;
  testDays: string;
  gameTestType: string;
  desiredTesterCount: string;
}

// 상태 추가: 각 입력 필드에 대한 오류 메시지

export default function AddTestPhase1() {
  const [userCountError, setUserCountError] = useState<string>("");

  // const gameProposalId = useRecoilValue(gameProposalIdState);
  const { gameProposalId } = useParams();
  const queryClient = useQueryClient();
  console.log("phase1 - gameProposalId : ", gameProposalId);


  const { data: gameProposalTestPlanDetailsData } = useQuery<GameProposalTestPlanDetailsResponse>({
    queryKey: ["gameProposalTestPlanDetails", gameProposalId],
    queryFn: async () => {
      const response = await LabsGameProposalTestPlanDetailsAPI(Number(gameProposalId));
      return response.data;
    },
    // initialData: {
    //   gameProposalId: 0,
    //   gameTestType: 'CBT',
    //   desiredTesterCount: 20,
    //   adultCheckRequired: true,
    //   recruitDays: 10,
    //   testDays: 70,
    //   rejections: {
    //     count: 0,
    //     reasons: {
    //       recruitDays: '',
    //       adultCheckRequired: '',
    //       testDays: '',
    //       gameTestType: '',
    //       desiredTesterCount: ''
    //     }
    //   }
    // },
    enabled: true
  })

  interface GameProposalDetailsResponse {
    gameProposalId: number;
    status: string;
    testPlanStatus: string;
    projectInfoStatus: string;
    questionCompositionStatus: string;
    previewAvailable: boolean;
    submitAvailable: boolean;
    gameName?: string;
  }

  const cachedGameProposalDetailsData = queryClient.getQueryData(["GameProposalOverview", gameProposalId]) as GameProposalDetailsResponse;
  console.log("** cachedGameProposalDetailsData : ", cachedGameProposalDetailsData);

  const { data: gameProposalDetailsData } = useQuery<GameProposalDetailsResponse>({
    queryKey: ["GameProposalOverview", gameProposalId],
    queryFn: async () => {
      const response = await GameProposalDetailsAPI(Number(gameProposalId));
      return response.data;
    },
    enabled: !cachedGameProposalDetailsData,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });


  const { data: gameProposalStepAvailabilityData = {
    testPlanAvailable: false,
    projectInfoAvailable: false,
    questionCompositionAvailable: false,
    estimateAvailable: false,
    previewAvailable: false,
    submitAvailable: false,
    testPlanSavable: false,
    projectInfoSavable: false,
    questionCompositionSavable: false
  } } = useQuery<GameProposalStepAvailabilityResponse>({
    queryKey: ["gameProposalStepAvailability", gameProposalId],
    queryFn: async () => {
      const response = await GameProposalStepAvailabilityAPI(Number(gameProposalId));
      return response.data;
    },
    // initialData : {
    //   "testPlanAvailable" : true,
    //   "projectInfoAvailable" : true,
    //   "questionCompositionAvailable" : false,
    //   "estimateAvailable" : false,
    //   "previewAvailable" : false,
    //   "submitAvailable" : true,
    //   "testPlanSavable" : true,
    //   "projectInfoSavable" : false,
    //   "questionCompositionSavable" : false
    // },
    enabled: true
  });

  const { register, setValue, getValues } = useForm();
  const [selectedPlatform, setSelectedPlatform] = useState<Platform>("CBT");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [adultCheckRequired, setAdultCheckRequired] = useState<boolean>(false);

  // 데이터가 로드되면 폼 값과 상태 초기화
  useEffect(() => {
    if (gameProposalTestPlanDetailsData) {
      // 게임 테스트 유형 설정
      setValue("platform", gameProposalTestPlanDetailsData.gameTestType);
      setSelectedPlatform(gameProposalTestPlanDetailsData.gameTestType as Platform);

      // 희망 테스트 유저 수 설정
      setValue("desiredTesterCount", gameProposalTestPlanDetailsData.desiredTesterCount);

      // 성인인증 설정
      setValue("adultCheckRequired", gameProposalTestPlanDetailsData.adultCheckRequired);
      setAdultCheckRequired(gameProposalTestPlanDetailsData.adultCheckRequired);

      // 모집 일수 설정
      setValue("recruitDays", gameProposalTestPlanDetailsData.recruitDays);

      // 테스트 일수 설정
      setValue("testDays", gameProposalTestPlanDetailsData.testDays);
    }
  }, [gameProposalTestPlanDetailsData, setValue]);

  // isDisabled 상태 추가
  const isDisabled = gameProposalStepAvailabilityData?.testPlanSavable === false;

  const minusUserCount = () => {
    if (isDisabled) return;
    const currentCount = getValues("desiredTesterCount") || 0;
    setValue("desiredTesterCount", Math.max(1, currentCount - 1));
  };

  const plusUserCount = () => {
    if (isDisabled) return;
    const currentCount = getValues("desiredTesterCount") || 0;
    setValue("desiredTesterCount", Number(currentCount) + 1);
  };

  const minusRecruitDays = () => {
    if (isDisabled) return;
    const currentRecruitDays = getValues("recruitDays") || 0;
    setValue("recruitDays", Math.max(5, currentRecruitDays - 1));
  };

  const plusRecruitDays = () => {
    if (isDisabled) return;
    const currentRecruitDays = getValues("recruitDays") || 0;
    setValue("recruitDays", Math.min(14, Number(currentRecruitDays) + 1));
  };

  const minusTestDays = () => {
    if (isDisabled) return;
    const currentTestDays = getValues('testDays') || 0;
    setValue('testDays', Math.max(7, currentTestDays - 1));
  };

  const plusTestDays = () => {
    if (isDisabled) return;
    const currentTestDays = getValues('testDays') || 0;
    setValue('testDays', Math.min(14, Number(currentTestDays) + 1));
  };



  const gameProposalTestPlanSaveMutation = useMutation<
    unknown,
    Error,
    { id: number; formData: FormData }
  >({
    mutationFn: ({ id, formData }) => GameProposalTestPlanSaveAPI(id, formData),
    onSuccess: (response: any) => {
      if (response.status === 200) {
        setShowSuccessModal(true);

        // 기존 쿼리 무효화
        queryClient.invalidateQueries({
          queryKey: ["gameProposalStepAvailability", gameProposalId]
        });

        // GameProposalStepAvailabilityAPI 다시 호출
        queryClient.refetchQueries({
          queryKey: ["gameProposalStepAvailability", gameProposalId]
        });
      }
    },
    onError: (error) => {
      console.error('Error:', error);
    }
  });

  const CallGameProposalTestPlanSaveAPI = async () => {
    const formData = {
      gameTestType: getValues("platform") !== 'CBT' ? 'NONE' : getValues("platform"),
      desiredTesterCount: getValues("desiredTesterCount"),
      adultCheckRequired: getValues("adultCheckRequired"),
      recruitDays: getValues("recruitDays"),
      testDays: getValues("testDays")
    };
    const check = CheckBody();

    console.log("phase1 - gameProposalId : ", gameProposalId);
    if (!check && gameProposalId) {
      gameProposalTestPlanSaveMutation.mutate({ id: Number(gameProposalId), formData });
    }
  };

  const CheckBody = useCallback(() => {
    const desiredTesterCount = getValues("desiredTesterCount");
    const recruitDays = getValues("recruitDays");
    const testDays = getValues("testDays");
    const platform = getValues("platform");

    const allFieldsFilled = desiredTesterCount &&
      recruitDays &&
      testDays &&
      platform == 'CBT';

    return !allFieldsFilled;
  }, [getValues]);

  const handlePlatformSelect = (platform: Platform) => {
    if (isDisabled) return;

    // 같은 버튼을 다시 클릭하면 선택 해제
    if (selectedPlatform === platform) {
      setSelectedPlatform(null);
    } else {
      setSelectedPlatform(platform);
    }
    setValue("platform", platform);
  };

  // 링크 클릭 핸들러 함수
  const handleNavLinkClick = (isAvailable: boolean) => (e: React.MouseEvent) => {
    if (!isAvailable) {
      e.preventDefault();
    }
  };

  // 스타일 컴포넌트 추가 (파일 상단 styled 컴포넌트 영역에 추가)
  const ErrorMessage = styled.p`
    color: #FF4444;
    font-size: 14px;
    margin-top: -90px;
    margin-bottom: 70px;
    padding-left: 5px;
  `;

  return (
    <>
      {/* <GlobalStyle/> */}
      <Main>
        <TopWrapper>
          <TopContainer>
            <Link to={`/home/test/draft/${gameProposalId}`} style={{ textDecoration: "none" }}>
              <TopTitle>
                <FontAwesomeIcon
                  icon={faChevronLeft}
                  style={{ marginRight: "20px" }}
                />
                1단계 테스트 플랜
              </TopTitle>
            </Link>
            <BtnContainer>
              {/* <PreviewBtn onClick={handlePreviewClick}>미리보기</PreviewBtn> */}
              <SaveBtn
                isActive={
                  (gameProposalStepAvailabilityData?.testPlanSavable ?? false)
                  && selectedPlatform === 'CBT' && userCountError === ''
                }
                onClick={CallGameProposalTestPlanSaveAPI}
              >
                저장하기
              </SaveBtn>
            </BtnContainer>
          </TopContainer>
          <NavCotainer>
            <CustomNavLink
              to={`/home/test-list/${gameProposalId}/add/first`}
              status={gameProposalStepAvailabilityData?.testPlanAvailable ?? false}
              isActive={true}
            >
              1. 테스트 플랜
            </CustomNavLink>

            <TooltipContainer>
            <CustomNavLink
              to={`/home/test-list/${gameProposalId}/add/second`}
              status={gameProposalStepAvailabilityData?.projectInfoAvailable ?? false}
              onClick={handleNavLinkClick(gameProposalStepAvailabilityData?.projectInfoAvailable ?? false)}
            >
              2. 프로젝트 정보
            </CustomNavLink>
            <Tooltip disabled={gameProposalStepAvailabilityData?.projectInfoAvailable !== true}>
            1단계 작성 후 저장하기를 눌러주세요
            </Tooltip>
            </TooltipContainer>
    
            <TooltipContainer>
            <CustomNavLink
              to={`/home/test-list/${gameProposalId}/add/third`}
              status={gameProposalStepAvailabilityData?.questionCompositionAvailable ?? false}
              onClick={handleNavLinkClick(gameProposalStepAvailabilityData?.questionCompositionAvailable ?? false)}
            >
              3. 질문 구성
            </CustomNavLink>
            <Tooltip disabled={gameProposalStepAvailabilityData?.questionCompositionAvailable !== true}>
            1단계 작성 후 저장하기를 눌러주세요
            </Tooltip>
            </TooltipContainer>

            <TooltipContainer>
            <CustomNavLink
              to={`/home/test-list/${gameProposalId}/add/forth`}
              status={gameProposalStepAvailabilityData?.estimateAvailable ?? false}
              onClick={handleNavLinkClick(gameProposalStepAvailabilityData?.estimateAvailable ?? false)}
            >
              4. 견적 확인
            </CustomNavLink>
            <Tooltip disabled={!gameProposalStepAvailabilityData?.estimateAvailable}>
              이전 단계의 필수값을 모두 입력해 주세요
            </Tooltip>
            </TooltipContainer>
          </NavCotainer>
        </TopWrapper>
        <ContentWrapper>
          <Title>
            게임 테스트 유형
            <Required> *</Required>
          </Title>
          <SubTitle>OBT의 경우 고객센터 문의하기로 연락주시기 바랍니다.</SubTitle>
          <LabelContainer>
            <PlatformButton
              isSelected={selectedPlatform === 'CBT'}
              onClick={() => handlePlatformSelect('CBT')}
              type="button"
              disabled={isDisabled}
            >
              CBT
            </PlatformButton>
          </LabelContainer>
          <Title>
            희망 테스트 유저 수<Required> *</Required>
          </Title>
          <SubTitle>원하시는 유저 테스트 희망 수를 설정해주세요.</SubTitle>
          <NumInputContainer>
            <NumBtn onClick={minusUserCount} disabled={isDisabled}>
              <Minus></Minus>
            </NumBtn>
            <NumInput
              type="number"
              defaultValue={gameProposalTestPlanDetailsData?.desiredTesterCount || 1}
              {...register("desiredTesterCount", {
                onChange: (e) => {
                  const value = parseInt(e.target.value);
                  if (isNaN(value)) {
                    setValue("desiredTesterCount", '');
                    setUserCountError("숫자값만 입력이 가능합니다.");
                  } else if (value < 1) {
                    setValue("desiredTesterCount", value);
                    setUserCountError("희망 테스트 유저수는 1명 이상 이어야 합니다.");
                  } else {
                    setValue("desiredTesterCount", value);
                    setUserCountError("");
                  }
                }
              })}
              disabled={isDisabled}
            />
            <NumBtn onClick={plusUserCount} disabled={isDisabled}>
              <Minus></Minus>
              <Plus></Plus>
            </NumBtn>
          </NumInputContainer>
          {userCountError && <ErrorMessage>{userCountError}</ErrorMessage>}
          {/* 희망 테스트 유저 수 반려 메시지 */}
          {gameProposalTestPlanDetailsData?.rejections?.reasons?.desiredTesterCount !== '' &&
            <span style={{ color: 'Red', fontSize: '13px', fontWeight: '400' }}>
              * 반려사유 : {gameProposalTestPlanDetailsData?.rejections?.reasons?.desiredTesterCount}
            </span>
          }
          {/* 간격 조절 */}
          <div style={{ height: '100px' }} />

          <Title>성인인증</Title>
          <SubTitle>성인 인증이 필요한 경우에 체크해주세요.</SubTitle>
          <CheckBoxContainer
            onClick={() => {

              if (isDisabled) return;
              const currentValue = getValues("adultCheckRequired");
              setValue("adultCheckRequired", !currentValue);
            }}
            disabled={isDisabled}
          >
            <CheckInput
              type="checkbox"
              id="check"
              checked={getValues("adultCheckRequired") || gameProposalTestPlanDetailsData?.adultCheckRequired}
              {...register("adultCheckRequired")}
              disabled={isDisabled}
            />
            <CheckBoxLabel htmlFor="check" onClick={() => {
                setValue("adultCheckRequired", !getValues("adultCheckRequired"));
            }}>
              <CheckBoxCheck></CheckBoxCheck>
            </CheckBoxLabel>
            <CheckBoxSpan>19세 미만 청소년은 참여할 수 없습니다.</CheckBoxSpan>
          </CheckBoxContainer>
          {/* 테스트 일수 반려 메시지 */}
          {gameProposalTestPlanDetailsData?.rejections?.reasons?.adultCheckRequired !== '' &&
            <span style={{ color: 'Red', fontSize: '13px', fontWeight: '400' }}>
              * 반려사유 : {gameProposalTestPlanDetailsData?.rejections?.reasons?.adultCheckRequired}

            </span>}

          {/* 간격 조절 */}
          <div style={{ height: '100px' }} />

          <Title>
            모집 일수<Required> *</Required>
          </Title>
          <SubTitle>
            원하는 유저 모집 일수를 설정해주세요.
            <br />
            최소 5일부터 최대 14일까지 설정 가능합니다.
          </SubTitle>
          <NumInputContainer>
            <NumBtn onClick={minusRecruitDays} disabled={isDisabled}>
              <Minus></Minus>
            </NumBtn>
            <NumInput
              type="number"
              value={getValues("recruitDays") || (gameProposalTestPlanDetailsData?.recruitDays || 5)}
              {...register("recruitDays")}
              disabled={isDisabled}
            />
            <NumBtn onClick={plusRecruitDays} disabled={isDisabled}>
              <Minus></Minus>
              <Plus></Plus>
            </NumBtn>

          </NumInputContainer>

          {/* 모집 일수 반려 메시지 */}
          {gameProposalTestPlanDetailsData?.rejections?.reasons?.recruitDays !== '' &&
            <span style={{ color: 'Red', fontSize: '13px', fontWeight: '400' }}>
              * 반려사유 : {gameProposalTestPlanDetailsData?.rejections?.reasons?.recruitDays}
            </span>}

          {/* 간격 조절 */}
          <div style={{ height: '100px' }} />

          <Title>
            테스트 일수<Required> *</Required>
          </Title>
          <SubTitle>원하는 테스트 일수를 설정해주세요.
            <br />
            최소 7일부터 최대 14일까지 설정 가능합니다.
          </SubTitle>
          <NumInputContainer>
            <NumBtn onClick={minusTestDays} disabled={isDisabled}>
              <Minus></Minus>
            </NumBtn>
            <NumInput
              type="number"
              value={getValues("testDays") || (gameProposalTestPlanDetailsData?.testDays || 7)}
              {...register("testDays")}
              disabled={isDisabled}
            />
            <NumBtn onClick={plusTestDays} disabled={isDisabled}>
              <Minus></Minus>
              <Plus></Plus>
            </NumBtn>
          </NumInputContainer>

          {/* 테스트 일수 반려 메시지 */}
          {gameProposalTestPlanDetailsData?.rejections?.reasons?.testDays !== '' &&
            <span style={{ color: 'Red', fontSize: '13px', fontWeight: '400' }}>
              * 반려사유 : {gameProposalTestPlanDetailsData?.rejections?.reasons?.testDays}
            </span>}
        </ContentWrapper>
      </Main>

      {/* 모달 컴포넌트 */}
      {showSuccessModal && (
        <SuccessModal onClose={() => setShowSuccessModal(false)} />
      )}
    </>
  );
}
